/**
 * SurveyJS custom element: step/page indicator (PADD-3866).
 *
 * Registers a non-value question type "stepindicator" that renders the shared
 * FormStepIndicator INSIDE the SurveyJS form. SurveyForm injects one instance at
 * the top of every page (when paging is on, there's more than one page, and the
 * form does not branch), so SurveyJS paints it right after the page title/description
 * and before the questions — the placement PADD-3866 asks for.
 *
 * It holds no answer data. State is derived at render time:
 *   - currentStep  = survey.currentPageNo         (each page re-renders on navigation)
 *   - stepCount    = survey.pageCount
 *   - errorStep    = survey.getPropertyValue("adErrorStep") (set by the host via the
 *                    SurveyForm ref; null when no page is in error)
 *
 * Registration runs as an import side effect (mirrors BooleanSlider / CustomSelect).
 */
import React from "react";
import { Question, ElementFactory, Serializer } from "survey-core";
import { SurveyQuestionElementBase, ReactQuestionFactory } from "survey-react-ui";

import FormStepIndicator from "./FormStepIndicator";

// Must be lowercase (SurveyJS element type convention).
export const STEP_INDICATOR_TYPE = "stepindicator";

// Model — a non-value display element. Extends Question (the integration base) but
// carries no value, and (via the properties set at injection time: titleLocation
// "hidden") renders no header, title, or "Optional" marker — just the widget.
export class QuestionStepIndicatorModel extends Question {
    getType() {
        return STEP_INDICATOR_TYPE;
    }
    // Never serialize/collect a value for this element.
    get hasInput() {
        return false;
    }
    // No title header at all — removes the auto-generated "__stepIndicator_N" label.
    get hasTitle() {
        return false;
    }
    getTitleLocation() {
        return "hidden";
    }
    // Suppress the "Optional" marker SurveyJS appends to non-required questions. These
    // getters are read by SurveyJS when deciding whether to render the optional/required
    // text, so overriding them removes the tag regardless of theme or who built the model.
    get isRequireTextOnStart() {
        return false;
    }
    get isRequireTextBeforeTitle() {
        return false;
    }
    get requiredText() {
        return "";
    }
    get optionalText() {
        return "";
    }
}

// JSON serialization: associate the "stepindicator" type with the model.
if (!Serializer.findClass(STEP_INDICATOR_TYPE)) {
    Serializer.addClass(
        STEP_INDICATOR_TYPE,
        [],
        function () {
            return new QuestionStepIndicatorModel("");
        },
        "question"
    );
}

// PADD-3866: an error-step property ON THE ELEMENT itself. Writing a question's own
// property makes SurveyJS re-render that question's React component — unlike a
// survey-level property, which does not reliably re-run a child element's renderElement
// in survey-react-ui 2.0.2. The host sets this on every indicator via the SurveyForm ref.
if (!Serializer.findProperty(STEP_INDICATOR_TYPE, "adErrorStep")) {
    Serializer.addProperty(STEP_INDICATOR_TYPE, { name: "adErrorStep:number", default: null });
}

// Factory registration so new QuestionStepIndicatorModel(name) can be created and so
// the element can be added to a page programmatically. Pass false to keep it out of
// the Survey Creator toolbox (it's injected by code, never authored by hand).
ElementFactory.Instance.registerElement(
    STEP_INDICATOR_TYPE,
    (name) => new QuestionStepIndicatorModel(name),
    false
);

// React renderer.
export class SurveyQuestionStepIndicator extends SurveyQuestionElementBase {
    get question() {
        return this.questionBase;
    }

    get survey() {
        return this.question?.survey;
    }

    componentDidMount() {
        // Re-render when the page changes so the active node tracks navigation. (The error
        // flag is read from the question's own adErrorStep property, which SurveyJS already
        // re-renders this element on when it changes — no manual subscription needed.)
        const survey = this.survey;
        if (!survey) return;
        this._onPageChanged = () => this.forceUpdate();
        survey.onCurrentPageChanged.add(this._onPageChanged);
    }

    componentWillUnmount() {
        const survey = this.survey;
        if (!survey) return;
        if (this._onPageChanged) survey.onCurrentPageChanged.remove(this._onPageChanged);
    }

    renderElement() {
        const survey = this.survey;
        if (!survey) return null;

        const stepCount = survey.pageCount;
        const currentStep = survey.currentPageNo;
        // Error flag comes from THIS element's own property so SurveyJS re-renders it.
        const errorStep = this.question.getPropertyValue("adErrorStep");

        return (
            <FormStepIndicator
                stepCount={stepCount}
                currentStep={currentStep}
                errorStep={errorStep === undefined ? null : errorStep}
            />
        );
    }
}

ReactQuestionFactory.Instance.registerQuestion(STEP_INDICATOR_TYPE, (props) => {
    return React.createElement(SurveyQuestionStepIndicator, props);
});
